// ----------------------------------------------------------------------

function path(root, sublink) {
    return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';
const ROOTS_STRIPE = '/stripe';
const ROOTS_FACEBOOK = '/facebook';
const ROOTS_GOOGLE = '/google';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
    root: ROOTS_AUTH,
    login: path(ROOTS_AUTH, '/login'),
    register: path(ROOTS_AUTH, '/register'),
    loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
    registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
    confirmEmail: path(ROOTS_AUTH, '/register/account-confirm-email'),
    verify: path(ROOTS_AUTH, '/verify'),
    resetPassword: path(ROOTS_AUTH, '/reset-password'),
};

export const PATH_PAGE = {
    maintenance: '/maintenance',
    page403: '/403',
    page404: '/404',
    page500: '/500',
    comingSoon: '/coming-soon',
    components: '/components',
    privacyPolicy: '/privacy-policy',
    newPassword: path(ROOTS_AUTH, '/reset/:uid/:token'),
    appointmentRating: '/appointments/feedback',
    consentForm: '/consent',
    formsPage: '/forms',
};

export const PATH_DASHBOARD = {
    root: ROOTS_DASHBOARD,
    general: {
        app: path(ROOTS_DASHBOARD, '/app'),
        leadDetail: path(ROOTS_DASHBOARD, '/practice/:practiceId/lead-detail/:id/:tab?'),
        archiveDetail: path(ROOTS_DASHBOARD, '/practice/:practiceId/archived/:id/'),
        StripeAuthorized: path(ROOTS_STRIPE, '/authorize/callback'),
        FacebookAuthorized: path(ROOTS_FACEBOOK, '/authorize/callback'),
        GoogleAuthorized: path(ROOTS_GOOGLE, '/authorize/callback'),
        GoogleAdsAuthorized: path(ROOTS_GOOGLE, '/ads/authorize/callback'),
    },
    task: {
        root: path(ROOTS_DASHBOARD, '/tasks'),
        v2Root: path(ROOTS_DASHBOARD, '/tasks-v2'),
        tasks: {
            root: (practiceId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/tasks-v2`),
            detail: (practiceId, taskId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/tasks-v2/${taskId}`),
            templates: (practiceId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/tasks-v2/templates`),
            templateNew: (practiceId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/tasks-v2/templates/new`),
            templateEdit: (practiceId, templateId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/tasks-v2/templates/${templateId}/edit`),
        },
    },
    whatsapp: {
        root: (practiceId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/whatsapp`),
        thread: (practiceId, threadId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/whatsapp/thread/${threadId}`)
    },
    sms: {
        root: path(ROOTS_DASHBOARD, '/sms')
    },
    emails: {
        root: path(ROOTS_DASHBOARD, '/emails')
    },
    callLogs: {
        root: path(ROOTS_DASHBOARD, '/call-logs')
    },
    instagram: {
        root: (practiceId) => path(ROOTS_DASHBOARD, `/practice/${practiceId}/instagram`),
    },
    createStripePayment: {
        root: path(ROOTS_DASHBOARD, '/payment-links/create')
    },
    analytics: {
        root: path(ROOTS_DASHBOARD, '/analytics'),
        reports: path(ROOTS_DASHBOARD, '/analytics/reports'),
        treatmentSummary: path(ROOTS_DASHBOARD, '/analytics/treatment-summary'),
        categorySummary: path(ROOTS_DASHBOARD, '/analytics/category-summary'),
        contactLogs: path(ROOTS_DASHBOARD, '/analytics/contact-logs'),
        sourceSummary: path(ROOTS_DASHBOARD, '/analytics/source-summary'),
        practitionerSummary: path(ROOTS_DASHBOARD, '/analytics/practitioner-summary'),
        reportsSetting: path(ROOTS_DASHBOARD, '/analytics/reports-setting'),
        archiveSummary: path(ROOTS_DASHBOARD, '/analytics/archived-summary'),
        allPracticeReports: path(ROOTS_DASHBOARD, '/analytics/allpractice-reports'),
    },
    analyticsReports: {
        root: path(ROOTS_DASHBOARD, '/analytics-reports'),
        overview: path(ROOTS_DASHBOARD, '/analytics-reports/overview'),
        allPractices: path(ROOTS_DASHBOARD, '/analytics-reports/all-practices'),
        byTreatment: path(ROOTS_DASHBOARD, '/analytics-reports/by-treatment'),
        byPractitioner: path(ROOTS_DASHBOARD, '/analytics-reports/by-practitioner'),
        bySource: path(ROOTS_DASHBOARD, '/analytics-reports/by-source'),
        trends: path(ROOTS_DASHBOARD, '/analytics-reports/trends'),
        whatsapp: path(ROOTS_DASHBOARD, '/analytics-reports/whatsapp'),
        googleAds: path(ROOTS_DASHBOARD, '/analytics-reports/google-ads'),
        googleAnalytics: path(ROOTS_DASHBOARD, '/analytics-reports/google-analytics'),
        marketing: path(ROOTS_DASHBOARD, '/analytics-reports/marketing'),
        marketingCampaigns: path(ROOTS_DASHBOARD, '/analytics-reports/campaigns'),
        metaAds: path(ROOTS_DASHBOARD, '/analytics-reports/meta-ads'),
        facebookAds: path(ROOTS_DASHBOARD, '/analytics-reports/facebook-ads'), // legacy alias — redirects to metaAds
        geoLocator: path(ROOTS_DASHBOARD, '/analytics-reports/geo-locator'),
        lostLeads: path(ROOTS_DASHBOARD, '/analytics-reports/lost-leads'),
        settings: path(ROOTS_DASHBOARD, '/analytics-reports/settings'),
        byTeam: path(ROOTS_DASHBOARD, '/analytics-reports/by-team'),
        dentallyInsights: path(ROOTS_DASHBOARD, '/analytics-reports/dentally-insights'),
        clickTracking: path(ROOTS_DASHBOARD, '/analytics-reports/click-tracking'),
    },
    groupDashboard: path(ROOTS_DASHBOARD, '/group-dashboard'),
    patientReview: path(ROOTS_DASHBOARD, '/patient-review'),
    tables: {
        root: path(ROOTS_DASHBOARD, '/tables'),
        AllLeadTable: path(ROOTS_DASHBOARD, '/tables/all-lead-table'),
        InterPractice: path(ROOTS_DASHBOARD, '/tables/inter-practice-table'),
        NoTaskLeadsTable: path(ROOTS_DASHBOARD, '/tables/no-task-assigned-table'),
        newLeadTable: path(ROOTS_DASHBOARD, '/tables/new-lead-table'),
        nhsLeadTable: path(ROOTS_DASHBOARD, '/tables/nhs-lead-table'),
        archiveLeadTable: path(ROOTS_DASHBOARD, '/tables/archive-table'),
        ConsultantAttendedTable: path(ROOTS_DASHBOARD, '/tables/consultant-attended-table'),
        ConsultantAttendedV2Table: path(ROOTS_DASHBOARD, '/tables/consultation-attended-v2'),
        ConsultantBookedTable: path(ROOTS_DASHBOARD, '/tables/consultant-booked-table'),
        treatmentStartedTable: path(ROOTS_DASHBOARD, '/tables/treatment-started-table'),
        alignerApproveTable: path(ROOTS_DASHBOARD, '/tables/aligner-approve-table'),
        failedToAttendTable: path(ROOTS_DASHBOARD, '/tables/failed-to-attend-table'),
        closedTable: path(ROOTS_DASHBOARD, '/tables/completed-table'),
        duplicateLeadTable: path(ROOTS_DASHBOARD, '/tables/duplicate-lead-table'),
        spamLeadTable: path(ROOTS_DASHBOARD, '/tables/spam-lead-table'),
        allPatients: path(ROOTS_DASHBOARD, '/tables/all-patients'),
        dentallyPatients: path(ROOTS_DASHBOARD, '/tables/dentally-patients'),
        attendedChecklistsTable: path(ROOTS_DASHBOARD, '/tables/attended-checklists-table'),
    },
    calendar: path(ROOTS_DASHBOARD, '/calendar'),
    kanban: path(ROOTS_DASHBOARD, '/kanban'),
    planner: path(ROOTS_DASHBOARD, '/planner'),
    marketingRequest: path(ROOTS_DASHBOARD, '/marketing-request'),
    websiteAnnotation: path(ROOTS_DASHBOARD, '/website-annotation'),
    websiteAnnotationView: (id) => path(ROOTS_DASHBOARD, `/website-annotation/${id}`),
    websiteAnnotationEdit: (id) => path(ROOTS_DASHBOARD, `/website-annotation/${id}/edit`),
    annotationSessions: path(ROOTS_DASHBOARD, '/annotation-sessions'),
    manageBusiness: path(ROOTS_DASHBOARD, '/manage-business'),
    googlePosts: path(ROOTS_DASHBOARD, '/google-posts'),
    financialTracker: path(ROOTS_DASHBOARD, '/financial-tracker'),
    qrCodeGenerator: path(ROOTS_DASHBOARD, '/qr-code'),
    survey: {
        root: path(ROOTS_DASHBOARD, '/survey'),
    },
    forms: {
        root: path(ROOTS_DASHBOARD, '/forms'),
    },
    storage: {
        root: path(ROOTS_DASHBOARD, '/storage'),
    },
    campaign: {
        root: path(ROOTS_DASHBOARD, '/campaigns-manager'),
        campaign: path(ROOTS_DASHBOARD, '/campaigns-manager/campaigns'),
        audienceManager: path(ROOTS_DASHBOARD, '/campaigns-manager/audience_manager'),
        createAudience: path(ROOTS_DASHBOARD, '/campaigns-manager/create_audience'),
        editAudience: (id) => path(ROOTS_DASHBOARD, `/campaigns-manager/${id}/edit_audience`),
        templates: path(ROOTS_DASHBOARD, '/campaigns-manager/templates'),
        builder: path(ROOTS_DASHBOARD, '/campaigns-manager/builder'),
        builderEdit: (id) => path(ROOTS_DASHBOARD, `/campaigns-manager/builder/${id}`),
        report: (campaignId) => path(ROOTS_DASHBOARD, `/campaigns-manager/campaigns/${campaignId}/report`),
        reports: path(ROOTS_DASHBOARD, '/campaigns-manager/reports'),
        reportsList: path(ROOTS_DASHBOARD, '/campaigns-manager/reports-list'),
        triggers: path(ROOTS_DASHBOARD, '/campaigns-manager/triggers'),
        segments: path(ROOTS_DASHBOARD, '/campaigns-manager/segments'),
        consentBroadcast: path(ROOTS_DASHBOARD, '/campaigns-manager/consent-broadcast'),
    },
    permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
    // Practice-scoped admin pages. JS-side accessor stays at
    // `PATH_DASHBOARD.user.X` for the moment (54 callers across the
    // codebase) — only the URL strings move. A backward-compat redirect
    // sends bookmarks at /dashboard/user/* to /dashboard/practice/*.
    // `/user/practice` (practice details editor) maps to `/practice/details`
    // to avoid the awkward `/practice/practice`.
    user: {
        root: path(ROOTS_DASHBOARD, '/practice'),
        new: path(ROOTS_DASHBOARD, '/practice/new'),
        list: path(ROOTS_DASHBOARD, '/practice/list'),
        profile: path(ROOTS_DASHBOARD, '/practice/profile'),
        account: path(ROOTS_DASHBOARD, '/practice/account'),
        message: path(ROOTS_DASHBOARD, '/practice/message'),
        integration: path(ROOTS_DASHBOARD, '/practice/integration'),
        practice: path(ROOTS_DASHBOARD, '/practice/details'),
        practiceId: (id) => path(ROOTS_DASHBOARD, `/practice/details/${id}`),
        practiceTreatment: path(ROOTS_DASHBOARD, '/practice/practice-treatment'),
        practiceUser: path(ROOTS_DASHBOARD, '/practice/practice-user'),
        edit: (name) => path(ROOTS_DASHBOARD, `/practice/${name}/edit`),
        consent: path(ROOTS_DASHBOARD, '/practice/practice-consent'),
        loan: path(ROOTS_DASHBOARD, '/practice/practice-loan'),
        notification: path(ROOTS_DASHBOARD, '/practice/notification-settings'),
        notificationPage: path(ROOTS_DASHBOARD, '/practice/notification-table'),
        practicePreferences: path(ROOTS_DASHBOARD, '/practice/practice-preferences'),
        demoEdit: path(ROOTS_DASHBOARD, '/practice/reece-chung/edit'),
        autoReminders: path(ROOTS_DASHBOARD, '/practice/auto-reminders'),
        createReminder: path(ROOTS_DASHBOARD, '/practice/create-reminder'),
        editAutoReminder: (id) => path(ROOTS_DASHBOARD, `/practice/${id}/edit-reminder`),
        facebookComment: path(ROOTS_DASHBOARD, '/practice/facebook'),
        exportToCsv: path(ROOTS_DASHBOARD, '/practice/export-csv'),
        practiceChatbot: path(ROOTS_DASHBOARD, '/practice/practice-chatbot'),
        callScripts: path(ROOTS_DASHBOARD, '/practice/call-scripts'),
        callScriptsV2: path(ROOTS_DASHBOARD, '/practice/call-scripts-v2'),
        createScript: path(ROOTS_DASHBOARD, '/practice/create-script'),
        createChatbot: path(ROOTS_DASHBOARD, '/practice/create-chatbot'),
        editCallScript: (id) => path(ROOTS_DASHBOARD, `/practice/${id}/edit-script`),
        editChatbot: (id) => path(ROOTS_DASHBOARD, `/practice/edit-chatbot/${id}`),
        consentBuilder: path(ROOTS_DASHBOARD, '/practice/consent'),
        createConsent: path(ROOTS_DASHBOARD, '/practice/create-consent'),
        editConsent: (id) => path(ROOTS_DASHBOARD, `/practice/${id}/edit-consent`),
        practitionerSchedule: (practitionerId) => path(ROOTS_DASHBOARD, `/practice/practitioner-schedule/${practitionerId}`),
        formSettings: path(ROOTS_DASHBOARD, '/practice/form-settings'),
    },
    /* super admin */
    superAdmin: {
        root: path(ROOTS_DASHBOARD, '/super-admin'),
        engagementTemplates: path(ROOTS_DASHBOARD, '/super-admin/engagement_templates'),
        EmailTemplates: path(ROOTS_DASHBOARD, '/super-admin/email_template/:templateId?'),
        SmsTemplates: path(ROOTS_DASHBOARD, '/super-admin/sms_template/:templateId?'),
        BulkAutoReminders: path(ROOTS_DASHBOARD, '/super-admin/auto_reminder'),
        CreateBulkAutoReminder: path(ROOTS_DASHBOARD, '/super-admin/create_reminder'),
        EditBulkAutoReminder: (id) => path(ROOTS_DASHBOARD, `/super-admin/${id}/edit_reminder`),
        ImportLeads: path(ROOTS_DASHBOARD, '/super-admin/import'),
        ImportLeadsCreate: path(ROOTS_DASHBOARD, '/super-admin/create_import_lead'),
        PracticeChoices: path(ROOTS_DASHBOARD, '/super-admin/practice-choices'),
        UserManagement: path(ROOTS_DASHBOARD, '/super-admin/user-management'),
        DomainManagement: path(ROOTS_DASHBOARD, '/super-admin/domain-management'),
        createPractice: path(ROOTS_DASHBOARD, '/super-admin/create-practice'),
        consent: path(ROOTS_DASHBOARD, '/super-admin/consent'),
        consentV2: path(ROOTS_DASHBOARD, '/super-admin/form-builder'),
        consentV2Create: path(ROOTS_DASHBOARD, '/super-admin/form-builder/create'),
        consentV2Edit: (id) => path(ROOTS_DASHBOARD, `/super-admin/form-builder/${id}/edit`),
        featureFlags: path(ROOTS_DASHBOARD, '/super-admin/feature-flags'),
        tasksV2Migration: path(ROOTS_DASHBOARD, '/super-admin/tasks-v2-migration'),
        tasksV2MigrationCockpit: (practiceId) => path(ROOTS_DASHBOARD, `/super-admin/tasks-v2-migration/${practiceId}`),
        tableDefaults: path(ROOTS_DASHBOARD, '/super-admin/table-defaults'),
        tableDefaultsPreview: path(ROOTS_DASHBOARD, '/super-admin/table-defaults-preview'),
        sidebarNavigation: path(ROOTS_DASHBOARD, '/super-admin/sidebar-navigation'),
        groupDashboard: path(ROOTS_DASHBOARD, '/super-admin/group-dashboard'),
        systemTaskTemplates: path(ROOTS_DASHBOARD, '/super-admin/system-task-templates'),
        notificationManagement: path(ROOTS_DASHBOARD, '/super-admin/notification-management'),
        notificationManagementHelp: path(ROOTS_DASHBOARD, '/super-admin/notification-management/help'),
    },
    invoice: {
        root: path(ROOTS_DASHBOARD, '/invoice'),
        list: path(ROOTS_DASHBOARD, '/invoice/list'),
        new: path(ROOTS_DASHBOARD, '/invoice/new'),
        view: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}`),
        edit: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}/edit`),
        demoEdit: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1/edit'),
        demoView: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5'),
    },
    // Geo Locator is a Reporting tab — see PATH_DASHBOARD.analyticsReports.geoLocator
    formSubmissions: path(ROOTS_DASHBOARD, '/form-submissions'),
    settings: {
        root: path(ROOTS_DASHBOARD, '/settings'),
        // Track E (2026-04-26): ``formDeliveryTemplates`` was retired —
        // templates are now managed under
        // ``PATH_DASHBOARD.campaignsManager.templates`` filtered by the
        // Form Delivery purpose chip.
    },
};

export const PATH_DOCS = 'https://docs-minimals.vercel.app/introduction';

export const PATH_PUBLIC = {
    consentForm: (formUuid) => `/consent/form/${formUuid}`,
    formsPage: (accessToken) => `/forms/${accessToken}`,
};
